import * as React from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, LinearProgress } from '@mui/material';
import PaginationTable from '../Pagination';
import { useContext, useEffect } from 'react';
import Box from '@mui/material/Box';
import { apiPlaylistProvider, PlaylistState } from '../../store/playlistSlice';
import { PlaylistVideoTable } from './PlaylistVideoTable';
import { getApiBaseUrl } from '../../utils/env';
import { AddVideoDialog } from '../modal/AddVideoDialog';
import { OrganisationContext } from '../../App';
import { useParams } from 'react-router-dom';
import PlaylistProperties from './PlaylistProperties';
import { RootState } from '../../index';
import { PlaylistVideo } from '../types/playlist';
import { useLocation } from 'react-router';
import PlaylistRoot from './PlaylistRoot';
import { VideoDetailedWithMF } from '../modal/VideoDetailedWithFM';

export default function PlaylistVideoList() {
  const { playlist } = useParams();
  const dispatch = useDispatch();
  const { selectedOrganisation } = useContext(OrganisationContext);
  const {
    formatFilter,
    videos,
    pagination,
    busy,
    currentPlaylist
  } = useSelector<RootState, PlaylistState>((state) => state.playlist);

  const { pathname } = useLocation();
  const [videoPreview, setVideoPreview] = React.useState<PlaylistVideo | null>();
  const handleClose = () => setVideoPreview(null);
  const [addVideoPreview, setAddVideoPreview] = React.useState<PlaylistVideo | {}>();
  const addHandleClose = () => setAddVideoPreview(undefined);

  const updateList = () => {
    dispatch<any>(
      apiPlaylistProvider({
        publisher: selectedOrganisation,
        limit: pagination.itemsPerPage,
        page: pagination.currentPage,
        selectedPlaylist: playlist,
        formatFilter,
      })
    );
  };

  const saveVideo = data => {
    addHandleClose();
    axios
      .post(`${getApiBaseUrl()}/playlist/${selectedOrganisation}/${playlist}`, {
        video_id: data.videoIdToAdd,
        pos: data.pos,
      })
      .then(response => {
        if (response.status === 201) {
          updateList();
        }
      })
      .catch(ex => {
        alert(ex);
      });
  };

  const removeVideo = (id: number) => {
    if (!window.confirm('Are you sure you want to remove this playlist video?')) {
      return;
    }
    axios.delete(`${getApiBaseUrl()}/playlist/${selectedOrganisation}/${playlist}/${id}`).then(() => {
      updateList();
    });
  };

  const updateAdBreakType = async (id: number, adBreakType: string | null, skippable: boolean) => {
    axios.post(`${getApiBaseUrl()}/playlist/${selectedOrganisation}/${playlist}/ad-break-type`, {
      id,
      ad_break_type: adBreakType,
      ad_break_skippable: adBreakType === 'preroll' ? skippable : false
    }).then(() => {
      updateList();
    });
  }

   const skipCustomCaption = async (id: number, skipCustomCaption: boolean) => {
    axios.post(`${getApiBaseUrl()}/playlist/${selectedOrganisation}/${playlist}/skip-custom-caption`, {
      id,
      skip_custom_caption: skipCustomCaption,
    }).then(() => {
      updateList();
    });
  }

  useEffect(() => {
    if (!selectedOrganisation) return;
    dispatch<any>(
      apiPlaylistProvider({
        publisher: selectedOrganisation,
        limit: pagination.itemsPerPage,
        page: pagination.currentPage,
        selectedPlaylist: playlist,
        formatFilter,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.itemsPerPage, pagination.currentPage, formatFilter, playlist, selectedOrganisation]);

  return videos ? (
    <PlaylistRoot>
      {videoPreview && (
        <VideoDetailedWithMF
          open={Boolean(videoPreview)}
          handleClose={handleClose}
          video={{
            video_type: videoPreview.video_type,
            media_id: videoPreview.video_id,
            parent_media_id: videoPreview.parent_media_id,
            parent_title: videoPreview.parent_title,
            parent_title_translated: videoPreview.parent_title_translated,
            publish_date: videoPreview.video_publish_date,
            rightsholder: videoPreview.video_rightsholder,
            format: videoPreview.video_format,
            title: videoPreview.video_title,
            summary: videoPreview.video_summary,
            has_variant: false, // implementation?
            article_title: videoPreview.article_title,
            article_thumbnail: videoPreview.article_thumbnail,
            can_manage_video: videoPreview.can_manage_video,
            transcripts: videoPreview.transcripts,
            uuid: videoPreview.uuid,
            disable_custom_caption: undefined,
            opus_title: videoPreview.opus_title,
            opus_description: videoPreview.opus_description,
            opus_clip_genre: videoPreview.opus_clip_genre,
            opus_hashtags: videoPreview.opus_hashtags,
          }}
        />
      )}
      {addVideoPreview && (
        <AddVideoDialog
          onSave={saveVideo}
          open={Boolean(addVideoPreview)}
          handleClose={addHandleClose}
          video={addVideoPreview}
        />
      )}

      {pathname === `/playlist/${playlist}` && <>
        {videos && (
          <Box sx={{ position: 'relative' }}>
            {busy && <LinearProgress sx={{ position: 'absolute', top: 0, width: '100%' }}
                                     color="success"/>}
            <PlaylistVideoTable
              videos={videos}
              onSetVideoPreview={setVideoPreview}
              onSetAddVideoPreview={setAddVideoPreview}
              onRemoveVideo={removeVideo}
              onAssignAdBreakType={updateAdBreakType}
              onSkipCustomCaption={skipCustomCaption}
              format={currentPlaylist?.format as string}
            />
          </Box>
        )}
        {busy && <LinearProgress color="success"/>}
        <PaginationTable
          defaultSize={20}
          busy={busy}
          startPage={pagination.currentPage - 1}
          count={pagination.totalItems}
          onChange={page =>
            dispatch({
              type: 'SET_PAGINATION',
              payload: {
                currentPage: page + 1,
              },
            })
          }
          updateRowPerPage={totalPerPage => {
            dispatch({
              type: 'SET_PAGINATION',
              payload: {
                currentPage: 1,
                itemsPerPage: totalPerPage,
              },
            });
          }}
        />
      </>}
      {pathname === `/playlist/properties/${playlist}` && <>
        <PlaylistProperties currentPlaylist={currentPlaylist} />
      </>}
    </PlaylistRoot>
  ) : (
    <CircularProgress style={{ padding: '5px' }}/>
  );
}
